import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, Popper, Typography, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { FEATURE_ONBOARDING_STATUS_QUERY, ONBOARDING_MUTATION, UX_FEATURE_QUERY } from './queries';
import { NOTES_POPOVER_FEATURE } from './PatientFloatingActionsHost';

const ONBOARDING_SHOWN = 'UNKNOWN';
const ONBOARDING_DISMISSED = 'DISMISSED';

interface Props {
  anchorEl: HTMLElement | null;
  shouldDismiss?: boolean;
}

const QuickNotesOnboarding = ({ anchorEl, shouldDismiss }: Props): JSX.Element | null => {
  const [visible, setVisible] = useState(false);
  const theme = useTheme();
  const visibleRef = useRef(false);
  const { pathname } = useLocation();

  useQuery(FEATURE_ONBOARDING_STATUS_QUERY, {
    variables: { featureName: NOTES_POPOVER_FEATURE },
    fetchPolicy: 'network-only',
    skip: !anchorEl,
    onCompleted: (data) => {
      if (data?.featureOnboardingStatus === ONBOARDING_SHOWN) {
        visibleRef.current = true;
        setVisible(true);
      }
    },
  });

  const { data: featureData } = useQuery(UX_FEATURE_QUERY, {
    variables: { featureName: NOTES_POPOVER_FEATURE },
    skip: !anchorEl,
  });

  const [updateOnboarding] = useMutation(ONBOARDING_MUTATION);

  const featureIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    featureIdRef.current = featureData?.uxFeature?.id;
  }, [featureData]);

  const dismiss = useCallback(() => {
    visibleRef.current = false;
    setVisible(false);
    if (featureIdRef.current) {
      updateOnboarding({ variables: { featureId: featureIdRef.current, status: ONBOARDING_DISMISSED } });
    }
  }, [updateOnboarding]);

  // Fires when the component unmounts — covers the navigate-away case where the parent
  // stops rendering this component before the visible/pathname effects can run.
  useEffect(() => {
    return () => {
      if (visibleRef.current && featureIdRef.current) {
        updateOnboarding({ variables: { featureId: featureIdRef.current, status: ONBOARDING_DISMISSED } });
      }
    };
  }, []);

  useEffect(() => {
    if (shouldDismiss && visible) {
      dismiss();
    }
  }, [shouldDismiss, visible, dismiss]);

  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname;
      if (visible) {
        dismiss();
      }
    }
  }, [pathname, visible, dismiss]);

  if (!visible || !anchorEl) return null;

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="top-end"
      modifiers={[{ name: 'offset', options: { offset: [0, 10] } }]}
      data-testid="quick-notes-onboarding-popover"
      sx={{ zIndex: (theme) => theme.zIndex.fab + 2, width: 444 }}>
      <Box
        sx={{
          bgcolor: '#394B68',
          borderRadius: '4px',
          position: 'relative',
          overflow: 'visible',
          padding: 1,
        }}>
        <Typography
          variant="h6"
          fontWeight={500}
          color={theme.palette.primary.contrastText}
          sx={{ padding: '8px 16px 16px 16px', margin: 0 }}
          gutterBottom>
          Add Notes from anywhere
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.primary.contrastText, padding: '0px 16px 16px 16px', margin: 0 }}
          fontWeight="400">
          Take notes while you work without switching screens. Drafts are saved automatically.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="text"
            size="small"
            onClick={dismiss}
            data-testid="quick-notes-onboarding-close"
            sx={{
              color: (theme) => theme.palette.primary.contrastText,
            }}>
            CLOSE
          </Button>
        </Box>
      </Box>
    </Popper>
  );
};

export default QuickNotesOnboarding;
